import { ChangeDetectorRef, Component, computed, effect, input, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Bind } from "../../decorators/bind.decorator";
import { S25Util } from "../../util/s25-util";
import { RowData } from "../s25-spreadsheet/types/sheet.js.types.expanded";
import { ScrollLockService } from "../../services/scroll.lock.service";
import { SpreadSheetService } from "../s25-spreadsheet/spreadsheet.service";
import { ImportType } from "./import.component";
import { ImportService, MasterDefinitionImportSaveResponseDto } from "./import.service";
import { ImportCustomAttributeTypesComponent } from "./import.custom.attribute.types.component";
import {
    MasterDefinitionImportKind,
    MasterDefinitionSpreadsheetUtil,
} from "./util/master-definition/master.definition.spreadsheet.util";

export interface MasterDefinitionSubtypeOption {
    key: MasterDefinitionImportKind;
    label: string;
}

@Component({
    selector: "s25-ng-import-master-definitions",
    host: { class: "s25-ng-import-master-definitions" },
    template: `
        <div class="c-margin-bottom--single">
            Upload a spreadsheet of master definitions. Choose a subtype, then include a Name column. Numeric cells are
            treated as existing IDs; prefix a numeric name with <code>$NAME-</code>.
        </div>

        <label class="c-margin-bottom--single">
            Definition type&nbsp;
            <select class="cn-form__control" [ngModel]="subtype()" (ngModelChange)="onSubtypeChange($event)">
                @for (option of subtypes; track option.key) {
                    <option [ngValue]="option.key">{{ option.label }}</option>
                }
            </select>
        </label>

        @if (isEventType()) {
            <div class="c-margin-bottom--single">
                Include <code>Node Type</code> (<code>C</code> cabinet type, <code>F</code> folder type, or
                <code>E</code> event type) and <code>Parent</code>. Leave Parent blank for cabinet types. Folder types
                must have a cabinet type parent. Event types must have a folder type parent, and cannot have a cabinet
                type parent. Import will not move types that already have a different parent.
            </div>
        }

        @if (isCustomAttribute()) {
            <div class="c-margin-bottom--single">
                Include a <code>Type</code> column with the single-character code.
                <s25-ng-import-custom-attribute-types />
            </div>
        }

        <div class="c-margin-bottom--single">
            <button class="aw-button aw-button--outline" (click)="downloadTemplate()">Download Excel Template</button>
        </div>
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ImportCustomAttributeTypesComponent],
})
export class S25ImportMasterDefinitionsComponent {
    readonly type = input.required<ImportType>();
    readonly parentCd = input<ChangeDetectorRef>();

    readonly subtypes: MasterDefinitionSubtypeOption[] = [
        { key: MasterDefinitionImportKind.EVENT_CATEGORY, label: "Event Category" },
        { key: MasterDefinitionImportKind.ORG_CATEGORY, label: "Organization Category" },
        { key: MasterDefinitionImportKind.LOCATION_CATEGORY, label: "Location Category" },
        { key: MasterDefinitionImportKind.RESOURCE_CATEGORY, label: "Resource Category" },
        { key: MasterDefinitionImportKind.EVENT_CUSTOM_ATTRIBUTE, label: "Event Custom Attribute" },
        { key: MasterDefinitionImportKind.ORG_CUSTOM_ATTRIBUTE, label: "Organization Custom Attribute" },
        { key: MasterDefinitionImportKind.CONTACT_CUSTOM_ATTRIBUTE, label: "Contact Custom Attribute" },
        { key: MasterDefinitionImportKind.LOCATION_CUSTOM_ATTRIBUTE, label: "Location Custom Attribute" },
        { key: MasterDefinitionImportKind.RESOURCE_CUSTOM_ATTRIBUTE, label: "Resource Custom Attribute" },
        { key: MasterDefinitionImportKind.OTHER_REQUIREMENT, label: "Other Requirement" },
        { key: MasterDefinitionImportKind.CALENDAR_REQUIREMENT, label: "Calendar Requirement" },
        { key: MasterDefinitionImportKind.EVENT_CONTACT_ROLE, label: "Event Contact Role" },
        { key: MasterDefinitionImportKind.ORG_CONTACT_ROLE, label: "Organization Contact Role" },
        { key: MasterDefinitionImportKind.ORG_TYPE, label: "Organization Type" },
        { key: MasterDefinitionImportKind.ORG_RATING, label: "Organization Rating" },
        { key: MasterDefinitionImportKind.FEATURE, label: "Location Feature" },
        { key: MasterDefinitionImportKind.LAYOUT, label: "Location Layout" },
        { key: MasterDefinitionImportKind.PARTITION, label: "Location Partition" },
        { key: MasterDefinitionImportKind.RATE_GROUP, label: "Rate Group" },
        { key: MasterDefinitionImportKind.EVENT_TYPE, label: "Event Type" },
    ];

    readonly subtype = signal(MasterDefinitionImportKind.EVENT_CATEGORY);
    readonly isEventType = computed(() => this.subtype() === MasterDefinitionImportKind.EVENT_TYPE);
    readonly isCustomAttribute = computed(() => MasterDefinitionSpreadsheetUtil.isCustomAttributeKind(this.subtype()));

    constructor(private readonly cd: ChangeDetectorRef) {
        effect(() => {
            this.type().onComplete = this.onComplete;
        });
    }

    onSubtypeChange(typeKey: MasterDefinitionImportKind) {
        this.subtype.set(typeKey);
        this.type().errors = [];
        this.type().successMessage = "";
        this.refresh();
    }

    @Bind
    async onComplete(rows: RowData[]) {
        try {
            this.type().errors = [];
            this.type().successMessage = "";

            if (!rows?.length) {
                this.type().errors.push("No sheet data found");
                this.refresh();
                return;
            }

            const errorSet = new Set<string>();
            const items = MasterDefinitionSpreadsheetUtil.rowsToItems(this.subtype(), rows, errorSet);
            if (errorSet.size) {
                this.type().errors = Array.from(errorSet);
                this.refresh();
                return;
            }
            if (!items.length) {
                this.type().errors.push("No valid master definition rows found");
                this.refresh();
                return;
            }

            const response = await ImportService.importMasterDefinitions({
                type: this.subtype(),
                items,
            });
            if (response?.error && response.error !== "no_items") {
                this.type().errors.push(
                    "Error importing master definitions: " + formatImportError(response.error) + ".",
                );
                if (response.rowErrors?.length) {
                    this.type().errors.push(...response.rowErrors);
                }
            } else {
                if (response?.rowErrors?.length) {
                    this.type().errors.push(...response.rowErrors);
                }
                this.type().successMessage = this.buildSuccessMessage(response);
            }
            this.refresh();
        } catch (caughtError) {
            const errorData = caughtError?.data || caughtError?.error;
            if (errorData?.error) {
                this.type().errors.push(errorData.error);
            } else {
                S25Util.showError(caughtError, "Failed to import master definitions");
            }
            this.refresh();
        }
    }

    async downloadTemplate() {
        const typeKey = this.subtype();
        const label = this.subtypes.find((option) => option.key === typeKey)?.label ?? typeKey;
        await SpreadSheetService.downloadExcel(
            [MasterDefinitionSpreadsheetUtil.templateRow(typeKey)],
            `Master_Definition_${typeKey}_Template.xlsx`,
            `${label} Import Template`,
        );
    }

    private buildSuccessMessage(response: MasterDefinitionImportSaveResponseDto | undefined): string {
        const created = response?.created ?? 0;
        const updated = response?.updated ?? 0;
        return `Imported master definitions (${created} created, ${updated} updated)`;
    }

    private refresh() {
        this.cd.detectChanges();
        this.parentCd()?.detectChanges();
        ScrollLockService.unlockBodyScroll();
    }
}

function formatImportError(error: string): string {
    if (error === "validation_failed") {
        return "Validation failed";
    }
    return error;
}
